import { getUser } from '../auth/authService';
import { tracked } from './workTelemetry';

const ANALYTICS = `${process.env.REACT_APP_ANALYTICS_MICROSERVICE_URL ?? ''}`;

async function authHeaders() {
    const user = await getUser();
    if (!user?.access_token) throw new Error('Not authenticated');
    return { 'Content-Type': 'application/json', Authorization: `Bearer ${user.access_token}` };
}

async function request(path, options = {}) {
    const res = await fetch(`${ANALYTICS}${path}`, {
        ...options, headers: { ...(await authHeaders()), ...(options.headers || {}) },
    });
    if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.detail || `HTTP ${res.status}`);
    }
    return res.json();
}

/**
 * How a meeting and a piece of work relate. These are not interchangeable:
 * "we said we would" is a promise made to a district, and is the one that
 * turns into an apology when it slips.
 */
export const RELATIONS = [
    { key: 'raised_at', label: 'Raised here', hint: 'The work came out of this meeting' },
    { key: 'discussed_at', label: 'Discussed', hint: 'Existing work got an update' },
    { key: 'promised_at', label: 'Promised', hint: 'We committed to it, to the client' },
    { key: 'prep_for', label: 'Prep for', hint: 'Must be done before this meeting' },
];

export const relationLabel = (key) =>
    (RELATIONS.find((r) => r.key === key) || {}).label || key;

/** Captured client meetings, each with the work linked to it. */
export const getMeetings = (clientId = '') =>
    request(`/api/meetings${clientId ? `?client_id=${encodeURIComponent(clientId)}` : ''}`);

/**
 * Client key dates a CSM marked "Show in Meetings Calendar" (ADO #4188), every
 * client the caller can see meetings for. Throws when they cannot be read, so
 * the page can say so instead of showing none.
 */
export const getKeyDates = () => request('/api/meetings/key-dates');

/** Domains a person has ruled on, and the clients available to rule them to. */
export const getMeetingCandidates = () => request('/api/meetings/candidates');

/** Rule on a domain: this client, or not a client at all. Both are answers. */
const decideDomainUntracked = (domain, clientId, note = '') =>
    request('/api/meetings/aliases', {
        method: 'POST',
        body: JSON.stringify({ domain, client_id: clientId || '', note }),
    });

/**
 * Rule on several domains at once — accepting suggestions, or "none of these
 * are clients". The server refuses any domain the caller cannot see.
 * decisions: [{ domain, client_id }] (client_id '' = not a client).
 */
const decideDomainsUntracked = (decisions, note = '') =>
    request('/api/meetings/aliases/bulk', {
        method: 'POST',
        body: JSON.stringify({ decisions, note }),
    });

/**
 * Move ONE meeting to the client it was really with.
 *
 * Not the same act as an alias, and the two are not interchangeable. An alias
 * rules on a domain and moves every meeting that domain ever touched; this
 * moves this meeting alone. Pass an empty list to say it was not a client
 * meeting at all — the capture is kept, it just stops appearing under a
 * district it had nothing to do with.
 */
const reattributeMeetingUntracked = (meetingId, clientIds, note = '') =>
    request(`/api/meetings/${encodeURIComponent(meetingId)}/attribution`, {
        method: 'POST',
        body: JSON.stringify({ client_ids: clientIds, note }),
    });

const linkWorkToMeetingUntracked = (meetingId, body) =>
    request(`/api/meetings/${encodeURIComponent(meetingId)}/links`, {
        method: 'POST', body: JSON.stringify(body),
    });

const unlinkWorkFromMeetingUntracked = (meetingId, source, itemId) =>
    request(`/api/meetings/${encodeURIComponent(meetingId)}/links`
        + `?source=${encodeURIComponent(source)}&item_id=${encodeURIComponent(itemId)}`,
        { method: 'DELETE' });

/**
 * {"source:item_id" -> links}, so a work row can say which meeting it came
 * out of without every row asking separately.
 */
/** Notes on one meeting, newest first. Rejects when the ledger can't be read. */
export const getMeetingNotes = (meetingId) =>
    request(`/api/meetings/${encodeURIComponent(meetingId)}/notes`).then((r) => r.notes);

const addMeetingNoteUntracked = (meetingId, text) =>
    request(`/api/meetings/${encodeURIComponent(meetingId)}/notes`, {
        method: 'POST', body: JSON.stringify({ text }),
    });

const removeMeetingNoteUntracked = (meetingId, noteId) =>
    request(`/api/meetings/${encodeURIComponent(meetingId)}/notes/${encodeURIComponent(noteId)}`, { method: 'DELETE' });

// Edit your own note (ADO #4318): a new ledger line; the earlier text stays.
const editMeetingNoteUntracked = (meetingId, noteId, text) =>
    request(`/api/meetings/${encodeURIComponent(meetingId)}/notes/${encodeURIComponent(noteId)}`, {
        method: 'PUT', body: JSON.stringify({ text }),
    });

// Attendees: Important flag + notes (ADO #4340). Scope 'meeting' = this
// attendee on this meeting; 'person' = the address, on every meeting it
// appears in. Reads reject when the ledger can't be read (never "nobody").
const peopleUrl = (meetingId, email = '') =>
    `/api/meetings/${encodeURIComponent(meetingId)}/people${email ? `/${encodeURIComponent(email)}` : ''}`;
export const getMeetingPeople = (meetingId) => request(peopleUrl(meetingId)).then((r) => r.people || {});
const setAttendeeImportantUntracked = (meetingId, email, important, scope) =>
    request(`${peopleUrl(meetingId, email)}/important`, { method: 'PUT', body: JSON.stringify({ important, scope }) });
const addAttendeeNoteUntracked = (meetingId, email, text, scope) =>
    request(`${peopleUrl(meetingId, email)}/notes`, { method: 'POST', body: JSON.stringify({ text, scope }) });
const editAttendeeNoteUntracked = (meetingId, email, noteId, text) =>
    request(`${peopleUrl(meetingId, email)}/notes/${encodeURIComponent(noteId)}`, { method: 'PUT', body: JSON.stringify({ text }) });
const removeAttendeeNoteUntracked = (meetingId, email, noteId) =>
    request(`${peopleUrl(meetingId, email)}/notes/${encodeURIComponent(noteId)}`, { method: 'DELETE' });

// How engaged each district attendee is with Nexus (ADO #4342): the same
// identity table and activity events Usage & Adoption reads. Rejects when a
// source can't be read (never zeros).
export const getMeetingEngagement = (meetingId, days = 30) =>
    request(`/api/meetings/${encodeURIComponent(meetingId)}/engagement?days=${days}`);

export const getWorkMeetingLinks = () => request('/api/meetings/links');

/**
 * How meetings are found — roster, cadence, window, the domains that count as
 * us or as personal mail — and what the last full hourly read saw, including
 * the domains it could not place. last_sweep is null when none is recorded.
 */
export const getMeetingAbout = () => request('/api/meetings/about');

/** Whose calendars the sweep reads. */
export const getRoster = () => request('/api/meetings/roster');

/**
 * Sweep one mailbox, or all of them.
 *
 * The UI sweeps them one at a time so the progress bar measures something
 * real. A single 40-second call can only be given a spinner, and a spinner
 * cannot tell you whether it is working or hung.
 */
const refreshMeetingsUntracked = (mailbox = '') =>
    request(`/api/meetings/refresh${mailbox ? `?mailbox=${encodeURIComponent(mailbox)}` : ''}`,
        { method: 'POST' });

/** Local-time day key, deliberately not toISOString — see workUi's mondayOf. */
export const dayKey = (iso) => {
    if (!iso) return '';
    const d = new Date(iso.endsWith('Z') || iso.includes('+') ? iso : `${iso}Z`);
    if (Number.isNaN(d.getTime())) return '';
    return [d.getFullYear(), String(d.getMonth() + 1).padStart(2, '0'),
        String(d.getDate()).padStart(2, '0')].join('-');
};

export const meetingTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso.endsWith('Z') || iso.includes('+') ? iso : `${iso}Z`);
    return Number.isNaN(d.getTime()) ? ''
        : d.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

export const daysSinceMeeting = (iso) => {
    if (!iso) return null;
    const d = new Date(iso.endsWith('Z') || iso.includes('+') ? iso : `${iso}Z`);
    if (Number.isNaN(d.getTime())) return null;
    return Math.floor((Date.now() - d.getTime()) / 86400000);
};

// --- Telemetry: every write above is recorded as a work_* event (services/workTelemetry.js).
export const decideDomains = tracked('meeting_domain_decide_bulk', decideDomainsUntracked, ([decisions, note]) => ({ resource: `domains:${decisions.length}`, details: { decisions, note } }));
export const decideDomain = tracked('meeting_domain_decide', decideDomainUntracked, ([domain, clientId, note]) => ({ resource: `domain:${domain}`, details: { domain, client_id: clientId, note } }));
export const reattributeMeeting = tracked('meeting_reattribute', reattributeMeetingUntracked, ([meetingId, clientIds, note]) => ({ resource: `meeting:${meetingId}`, details: { meeting_id: meetingId, client_ids: clientIds, note } }));
export const linkWorkToMeeting = tracked('meeting_link', linkWorkToMeetingUntracked, ([meetingId, body]) => ({ resource: `meeting:${meetingId}`, details: { meeting_id: meetingId, ...body } }));
export const unlinkWorkFromMeeting = tracked('meeting_unlink', unlinkWorkFromMeetingUntracked, ([meetingId, source, itemId]) => ({ resource: `meeting:${meetingId}`, details: { meeting_id: meetingId, source, item_id: String(itemId) } }));
export const refreshMeetings = tracked('meeting_refresh', refreshMeetingsUntracked, ([mailbox]) => ({ resource: 'meetings', details: { mailbox } }));
export const addMeetingNote = tracked('meeting_note_add', addMeetingNoteUntracked, ([meetingId, text]) => ({ resource: `meeting:${meetingId}`, details: { meeting_id: meetingId, chars: (text || '').length } }));
export const editMeetingNote = tracked('meeting_note_edit', editMeetingNoteUntracked, ([meetingId, noteId, text]) => ({ resource: `meeting:${meetingId}`, details: { meeting_id: meetingId, note_id: noteId, chars: (text || '').length } }));
export const removeMeetingNote = tracked('meeting_note_remove', removeMeetingNoteUntracked, ([meetingId, noteId]) => ({ resource: `meeting:${meetingId}`, details: { meeting_id: meetingId, note_id: noteId } }));
export const setAttendeeImportant = tracked('meeting_attendee_important', setAttendeeImportantUntracked, ([meetingId, email, important, scope]) => ({ resource: `meeting:${meetingId}`, details: { meeting_id: meetingId, email, important, scope } }));
export const addAttendeeNote = tracked('meeting_attendee_note_add', addAttendeeNoteUntracked, ([meetingId, email, text, scope]) => ({ resource: `meeting:${meetingId}`, details: { meeting_id: meetingId, email, scope, chars: (text || '').length } }));
export const editAttendeeNote = tracked('meeting_attendee_note_edit', editAttendeeNoteUntracked, ([meetingId, email, noteId, text]) => ({ resource: `meeting:${meetingId}`, details: { meeting_id: meetingId, email, note_id: noteId, chars: (text || '').length } }));
export const removeAttendeeNote = tracked('meeting_attendee_note_remove', removeAttendeeNoteUntracked, ([meetingId, email, noteId]) => ({ resource: `meeting:${meetingId}`, details: { meeting_id: meetingId, email, note_id: noteId } }));
